import { useCallback, useEffect, useState } from 'react'
import { Header } from './components/Header'
import { Hero } from './components/Hero'
import { OperatorCredibility } from './components/OperatorCredibility'
import { ArchitectureStrip } from './components/ArchitectureStrip'
import { Sizer } from './components/Sizer'
import { WhyCyLogic } from './components/WhyCyLogic'
import { WorkloadFitness } from './components/WorkloadFitness'
import { Migration } from './components/Migration'
import { Compare } from './components/Compare'
import { CapabilityStatement } from './components/CapabilityStatement'
import { TalkToUs } from './components/TalkToUs'
import { Footer } from './components/Footer'
import { LeadModal, type InquiryType } from './components/LeadModal'

/**
 * Home view — landing page for cycloud-public.pages.dev.
 *
 * Sprint 21 §1.10 moved the prior hand-rolled pathname-switch router
 * out of this file and into router.tsx (TanStack Router). The three
 * route paths `/`, `/compare`, `/methodology` all render this view
 * for now; per-route deep-link behavior (e.g. autoscroll to the
 * methodology section on /methodology load) lands with the §4
 * frontend build.
 */

function scrollToId(id: string) {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const BOOKINGS_URL = (import.meta.env.VITE_MICROSOFT_BOOKINGS_URL as string | undefined) ?? ''

export function HomeView() {
  const [modalOpen, setModalOpen] = useState(false)
  const [inquiryType, setInquiryType] = useState<InquiryType>('general')

  // Sprint 21 §4: react to deep-link routes. On /compare, autoscroll to
  // the compare section; on /methodology, autoscroll to the methodology
  // anchor (rendered by §4.8). Until §4 lands the methodology anchor,
  // /methodology falls through to top-of-page like /.
  useEffect(() => {
    const path = window.location.pathname
    if (path === '/compare') scrollToId('compare')
    else if (path === '/methodology') scrollToId('methodology')
  }, [])

  const openModal = useCallback((t: InquiryType) => {
    setInquiryType(t)
    setModalOpen(true)
  }, [])

  const onArchitectureReview = useCallback(() => {
    if (BOOKINGS_URL) {
      window.open(BOOKINGS_URL, '_blank', 'noopener,noreferrer')
    } else {
      openModal('architecture_review')
    }
  }, [openModal])

  return (
    <div className="min-h-screen flex flex-col">
      <Header onSizer={() => scrollToId('sizer')} onTalk={() => openModal('general')} />
      <Hero
        onSizeWorkload={() => scrollToId('sizer')}
        onTalkToArchitect={onArchitectureReview}
      />
      <OperatorCredibility />
      <ArchitectureStrip />
      <Sizer onJumpToCompare={() => scrollToId('compare')} />
      <WhyCyLogic />
      <WorkloadFitness />
      <Migration onScheduleAssessment={onArchitectureReview} />
      <Compare />
      <CapabilityStatement onRequest={() => openModal('capability_statement')} />
      <TalkToUs
        onCaptureTeaming={() => openModal('capture_teaming')}
        onArchitectureReview={onArchitectureReview}
      />
      <Footer
        onScrollToSizer={() => scrollToId('sizer')}
        onArchitectGpu={onArchitectureReview}
        onContact={() => openModal('general')}
        onCapStatement={() => openModal('capability_statement')}
      />

      <LeadModal
        open={modalOpen}
        defaultInquiryType={inquiryType}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
